import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Eyebrow, Reveal } from "../components/Shared";
import { COMMITMENTS } from "../data/site";

const Counter = ({ target }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, target, {
      duration: 1.6,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, target]);

  return (
    <span ref={ref} data-testid="team-counter">
      {value}
    </span>
  );
};

const CommitmentSection = () => (
  <section className="relative overflow-hidden bg-canvas py-24 sm:py-32" data-testid="commitment-section">
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <div>
          <Reveal>
            <Eyebrow>05 — Customer Commitment</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-2xl font-bold leading-tight tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Your Vision.
              <br />
              Our Responsibility.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate sm:text-lg">
              Every project begins with understanding the client's vision — and ends only when expectations are
              exceeded.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c} delay={0.1 + i * 0.06}>
                <div className="flex items-center gap-3" data-testid={`commitment-${i + 1}`}>
                  <CheckCircle2 size={18} className="shrink-0 text-brand2" />
                  <span className="text-sm font-medium text-navy">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.2}>
          <div className="card relative overflow-hidden p-10 sm:p-14" data-testid="team-stat-card">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-tint" />
            <div className="relative">
              <p className="font-display text-7xl font-bold tracking-tight text-navy sm:text-8xl lg:text-9xl">
                <Counter target={30} />
              </p>
              <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand">
                Dedicated Professionals
              </p>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate">
                A team of 30 dedicated professionals takes personal pride in every single sheet that leaves our
                presses.
              </p>
            </div>
            <span className="absolute bottom-0 left-0 h-1 w-24 rounded-tr-full bg-brand2" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default CommitmentSection;
